import colors from '../../theme/colors';
import PropTypes from 'prop-types';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Security as SecurityIcon,
  Build as BuildIcon,
  ContactSupport as ContactSupportIcon,
  LightbulbOutlined as SuggestionIcon,
} from '@mui/icons-material';

/**
 * ErrorDisplay - A comprehensive error display component for the QSAI application
 *
 * This component handles structured error responses from the backend and presents
 * them in a user-friendly format with appropriate styling, icons, and actionable suggestions.
 *
 * @component
 * @example
 * // Basic usage with structured error
 * const error = {
 *   code: "PDF_PAGE_LIMIT_EXCEEDED",
 *   type: "validation_error",
 *   message: "Technical message",
 *   user_message: "User-friendly message",
 *   suggestions: ["suggestion1", "suggestion2"],
 *   details: {...}
 * };
 *
 * <ErrorDisplay
 *   error={error}
 *   onContactSupport={handleContactSupport}
 *   showSuggestions={true}
 * />
 */
const ErrorDisplay = ({
  error,
  onContactSupport,
  showSuggestions = true,
  showContactSupport = true,
  showErrorCode = true,
  showErrorDetails = false,
  variant = 'standard',
  sx = {},
}) => {
  // Default values and error parsing
  const errorCode = error?.code || 'UNKNOWN_ERROR';
  const errorType = error?.type || 'system_error';
  const userMessage =
    error?.user_message || error?.message || 'An unexpected error occurred';
  const suggestions = error?.suggestions || [];
  const details = error?.details || {};
  const referenceId = error?.reference_id || Date.now().toString(36);

  // Get error severity and icon based on type
  const getErrorSeverity = (type) => {
    switch (type) {
      case 'validation_error':
        return { severity: 'warning', icon: <WarningIcon /> };
      case 'permission_error':
        return { severity: 'error', icon: <SecurityIcon /> };
      case 'business_error':
        return { severity: 'info', icon: <InfoIcon /> };
      case 'system_error':
      default:
        return { severity: 'error', icon: <ErrorIcon /> };
    }
  };

  // Get error color scheme
  const getErrorColors = (type) => {
    switch (type) {
      case 'validation_error':
        return {
          primary: colors.status.pending,
          background: colors.orange.lightBg,
          chip: colors.orange.light,
        };
      case 'permission_error':
        return {
          primary: colors.red.main,
          background: colors.red.lighter,
          chip: colors.red.light,
        };
      case 'business_error':
        return {
          primary: colors.status.info,
          background: colors.blue.lighter,
          chip: colors.blue.main,
        };
      case 'system_error':
      default:
        return {
          primary: colors.red.main,
          background: colors.red.lighter,
          chip: colors.red.light,
        };
    }
  };

  const { severity, icon } = getErrorSeverity(errorType);
  const errorColors = getErrorColors(errorType);

  // Render error code chip
  const renderErrorCode = () => {
    if (!showErrorCode) return null;

    return (
      <Box sx={{ mb: 1 }}>
        <Chip
          label={errorCode}
          size="small"
          sx={{
            backgroundColor: errorColors.chip,
            color: errorColors.background.paper,
            fontWeight: 'bold',
            fontSize: '11px',
            fontFamily: 'monospace',
          }}
        />
      </Box>
    );
  };

  // Render suggestions list
  const renderSuggestions = () => {
    if (!showSuggestions || !suggestions.length) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            mb: 1,
            color: errorColors.primary,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontFamily: 'RooneySans, sans-serif',
          }}
        >
          <SuggestionIcon fontSize="small" />
          Suggestions
        </Typography>
        <List dense sx={{ py: 0 }}>
          {suggestions.map((suggestion, index) => (
            <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <BuildIcon
                  fontSize="small"
                  sx={{ color: errorColors.primary }}
                />
              </ListItemIcon>
              <ListItemText
                primary={suggestion}
                primaryTypographyProps={{
                  fontSize: '14px',
                  fontFamily: 'RooneySans, sans-serif',
                }}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  // Render error details (for debugging)
  const renderErrorDetails = () => {
    if (!showErrorDetails || !Object.keys(details).length) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            mb: 1,
            color: errorColors.primary,
            fontFamily: 'RooneySans, sans-serif',
          }}
        >
          Error Details
        </Typography>
        <Paper
          sx={{
            p: 1,
            backgroundColor: errorColors.background,
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography
            variant="body2"
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '12px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              margin: 0,
            }}
          >
            {JSON.stringify(details, null, 2)}
          </Typography>
        </Paper>
      </Box>
    );
  };

  // Render contact support button
  const renderContactSupport = () => {
    if (!showContactSupport || !onContactSupport) return null;

    return (
      <Button
        variant="outlined"
        size="small"
        startIcon={<ContactSupportIcon />}
        onClick={onContactSupport}
        sx={{
          borderColor: errorColors.primary,
          color: errorColors.primary,
          fontFamily: 'RooneySans, sans-serif',
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': {
            backgroundColor: errorColors.background,
            borderColor: errorColors.primary,
          },
        }}
      >
        Contact Support
      </Button>
    );
  };

  // Render reference ID
  const renderReferenceId = () => (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        mt: 1,
        color: 'text.secondary',
        fontFamily: 'monospace',
        fontSize: '11px',
      }}
    >
      Reference ID: {referenceId}
    </Typography>
  );

  // Compact variant for inline errors
  if (variant === 'compact') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          borderRadius: 1,
          backgroundColor: errorColors.background,
          border: `1px solid ${errorColors.primary}`,
          ...sx,
        }}
      >
        {icon}
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            fontFamily: 'RooneySans, sans-serif',
            color: errorColors.primary,
          }}
        >
          {userMessage}
        </Typography>
        {showErrorCode && (
          <Chip
            label={errorCode}
            size="small"
            sx={{
              backgroundColor: errorColors.chip,
              color: errorColors.background.paper,
              fontSize: '10px',
              height: 20,
            }}
          />
        )}
      </Box>
    );
  }

  // Standard variant with full error display
  return (
    <Alert
      severity={severity}
      icon={icon}
      sx={{
        mt: 2,
        fontFamily: 'RooneySans, sans-serif',
        '& .MuiAlert-message': {
          width: '100%',
        },
        '& .MuiAlert-icon': {
          fontSize: '24px',
        },
        ...sx,
      }}
      action={
        showContactSupport && onContactSupport ? (
          <Box sx={{ display: 'flex', gap: 1 }}>{renderContactSupport()}</Box>
        ) : undefined
      }
    >
      {renderErrorCode()}

      <AlertTitle
        sx={{
          fontFamily: 'RooneySans, sans-serif',
          fontWeight: 600,
          whiteSpace: 'pre-wrap',
        }}
      >
        {userMessage}
      </AlertTitle>

      {renderSuggestions()}
      {renderErrorDetails()}
      {renderReferenceId()}
    </Alert>
  );
};

ErrorDisplay.propTypes = {
  /**
   * Error object containing structured error information
   * @type {Object}
   * @property {string} code - Error code identifier
   * @property {string} type - Error type (validation_error, business_error, system_error, permission_error)
   * @property {string} message - Technical error message
   * @property {string} user_message - User-friendly error message
   * @property {Array<string>} suggestions - Array of actionable suggestions
   * @property {Object} details - Additional error details
   * @property {string} reference_id - Error reference ID
   */
  error: PropTypes.shape({
    code: PropTypes.string,
    type: PropTypes.oneOf([
      'validation_error',
      'business_error',
      'system_error',
      'permission_error',
    ]),
    message: PropTypes.string,
    user_message: PropTypes.string,
    suggestions: PropTypes.arrayOf(PropTypes.string),
    details: PropTypes.object,
    reference_id: PropTypes.string,
  }).isRequired,

  /**
   * Function to call when contact support button is clicked
   */
  onContactSupport: PropTypes.func,

  /**
   * Whether to show suggestions section
   */
  showSuggestions: PropTypes.bool,

  /**
   * Whether to show contact support button
   */
  showContactSupport: PropTypes.bool,

  /**
   * Whether to show error code chip
   */
  showErrorCode: PropTypes.bool,

  /**
   * Whether to show detailed error information (for debugging)
   */
  showErrorDetails: PropTypes.bool,

  /**
   * Display variant
   */
  variant: PropTypes.oneOf(['standard', 'compact']),

  /**
   * Additional Material-UI sx props for styling
   */
  sx: PropTypes.object,
};

export default ErrorDisplay;
